import React, { useEffect, useState } from 'react';
import 'intersection-observer';
import { useInView } from 'react-intersection-observer';

import injectScript from '../utils/inject-script';
import { runOnBreakpointChange } from '../utils/breakpoints-service';

import './maps.scss';

export interface Props {
    height: string;
    lat: any;
    long: any;
    mapApi: string;
    refreshMap?: boolean;
    width: string;
    zoom?: number;
    disableDragging?: boolean;
}

const Maps: React.FC<Props> = ({
    refreshMap = true,
    mapApi,
    zoom = 14,
    lat,
    long,
    width = '100%',
    height = '200px',
    disableDragging = false,
}) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [mapRef, inView] = useInView({ threshold: 0.5 });
    const [hasIntersection, setIntersection] = useState(false);

    const [mapState, setMap] = useState('' as any);

    const base = '//maps.googleapis.com/maps/api/js';
    const callback = '&callback=initMap';

    const addCircle = () => {
        new window.google.maps.Circle({
            strokeWeight: 0,
            fillColor: '#007AFF',
            fillOpacity: 0.1,
            map: mapState,
            center: mapState.center,
            radius: 400,
        });
    };

    const initMap = () => {
        if (mapState === '' && ref.current) {
            const map = new window.google.maps.Map(ref.current, {
                zoom,
                streetViewControl: false,
                center: {
                    lat: parseFloat(lat),
                    lng: parseFloat(long),
                },
                gestureHandling: disableDragging ? 'none' : 'auto',
            });

            setMap(map);

            return map;
        } else {
            return mapState;
        }
    };

    const loadJS = (src) => {
        const options = {
            async: true,
            defer: true,
        };

        injectScript(src, options);
    };

    const refreshMapFunc = () => {
        window.google.maps.event.trigger(mapState, 'resize');
    };

    useEffect(() => {
        typeof window !== 'undefined' && window.IntersectionObserver && setIntersection(true);
    }, [hasIntersection]);

    useEffect(() => {
        mapState !== '' && addCircle();
    }, [mapState]);

    useEffect(() => {
        (window as any).initMap = initMap;

        if (hasIntersection && inView) {
            if (!(window.google && window.google.maps) && mapApi) {
                loadJS(`${base}?key=${mapApi}${callback}`);
            } else {
                initMap();
            }
        }

        if (refreshMap) {
            runOnBreakpointChange(refreshMapFunc);
        }
    }, [inView]);

    return (
        <div ref={mapRef}>
            <div className="google-map" ref={ref} style={{ width, height }} data-q="google-map" />
        </div>
    );
};

export default Maps;
